import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCallCenterOptional, isSameCall } from '../../contexts/CallCenterContext';
import { useSocket, useSocketEvent } from '../../hooks/useSocket';
import CallScreenPopModal from './CallScreenPopModal';

/**
 * گوش دادن به رویدادهای زنده مرکز تماس از طریق Socket.io (karmadws)
 * به محض ارسال وب‌هوک توسط استریسک، پنجره Screen Pop برای اپراتور مربوطه باز می‌شود.
 */
const CallScreenPopPoller = () => {
  const { user, isAuthenticated } = useAuth();
  const callCenter = useCallCenterOptional();
  const { emit, isConnected } = useSocket();
  const recentEventsRef = useRef(new Map());
  const loadExtRef = useRef(false);

  const isActive = Boolean(isAuthenticated && callCenter?.isCallCenterActive);

  // لود داخلی اپراتور در صورت لاگین
  useEffect(() => {
    if (isActive && !loadExtRef.current && callCenter?.loadMyAgentExtension) {
      loadExtRef.current = true;
      callCenter.loadMyAgentExtension();
    }
  }, [isActive, callCenter]);

  // ثبت و پیوستن به اتاق‌های داخلی و شناسه اپراتور در سوکت به محض برقراری اتصال
  useEffect(() => {
    if (!isActive || !isConnected) return;

    if (user?.id) {
      emit('join-agent-room', user.id);
      emit('join-callcenter-agents', user?.company_id);
    }

    const exts = (
      Array.isArray(callCenter?.myAgentExtensions) && callCenter.myAgentExtensions.length > 0
        ? callCenter.myAgentExtensions
        : [callCenter?.myAgentExtension]
    ).filter(Boolean);

    exts.forEach((ext) => {
      emit('join-extension', String(ext).trim());
    });
  }, [isActive, isConnected, user?.id, user?.company_id, callCenter?.myAgentExtension, callCenter?.myAgentExtensions, emit]);

  /**
   * بررسی دقیق اینکه آیا تماس مربوط به این اپراتور است یا خیر.
   */
  const isCallForCurrentAgent = (data) => {
    if (!data) return false;

    // ۱. بررسی جهت تماس: پاپ‌آپ فقط برای تماس‌های ورودی (incoming / inbound) است
    const direction = String(data.direction || data.call?.direction || '').toLowerCase().trim();
    if (direction && direction !== 'incoming' && direction !== 'inbound') {
      console.log(`[Socket] Suppressed popup for non-incoming call (direction: ${direction})`);
      return false;
    }

    // ۲. جداسازی کامل چندشرکتی (Multi-tenant Company Isolation):
    // تماس‌های هر شرکت منحصراً باید برای اپراتورهای همان شرکت نمایش داده شوند
    const eventCompanyId = data.company_id || data.call?.company_id;
    const userCompanyId = user?.company_id;
    if (eventCompanyId && userCompanyId && String(eventCompanyId) !== String(userCompanyId)) {
      console.log(`[Socket] Suppressed popup for different company (Call Company: ${eventCompanyId} vs User Company: ${userCompanyId})`);
      return false;
    }

    // ۳. اگر تنظیم پخش پاپ‌آپ برای تمام اپراتورهای آنلاین فعال باشد (broadcast_popup_to_all)
    if (data.broadcast_to_all || data.broadcast_popup_to_all || data.call?.broadcast_to_all) {
      return true;
    }

    const currentUserId = user?.id ? Number(user.id) : null;
    const targetAgentIds = Array.isArray(data.agent_id)
      ? data.agent_id.map(Number)
      : (data.agent_id ? [Number(data.agent_id)] : []);

    if (data.call?.agent_user_id) {
      targetAgentIds.push(Number(data.call.agent_user_id));
    }

    // ۴. اگر تماس مستقیماً برای این اپراتور ارسال شده است (اولویت اول)
    if (currentUserId && targetAgentIds.includes(currentUserId)) {
      return true;
    }

    // ۵. بررسی بر اساس شماره داخلی اپراتور (در صورتی که agent_id مستقیم ارسال نشده باشد)
    const eventExt = String(data.extension || data.call?.extension || '').trim();
    const myExts = (
      Array.isArray(callCenter?.myAgentExtensions) && callCenter.myAgentExtensions.length > 0
        ? callCenter.myAgentExtensions
        : [callCenter?.myAgentExtension]
    )
      .filter(Boolean)
      .map((e) => String(e).trim());

    if (eventExt && myExts.length > 0) {
      return myExts.includes(eventExt);
    }

    return false;
  };

  /**
   * باز کردن یا به‌روزرسانی پاپ‌آپ تماس ورودی
   */
  const handleIncomingCall = (data, eventName = 'ringing') => {
    if (!callCenter || !data) return;
    if (!isCallForCurrentAgent(data)) return;

    const rawId = String(data.call_id || data.call?.id || data.uniqueid || data.simotel_uniqueid || '').trim();
    const callPhone = String(data.phone || data.call?.phone || '').trim();
    const now = Date.now();
    const debounceKey = `${rawId || 'call'}_${callPhone}_${eventName}`;
    const lastSeen = recentEventsRef.current.get(debounceKey) || 0;

    // Deduplicate same event within 1000ms
    if (now - lastSeen < 1000) {
      return;
    }
    recentEventsRef.current.set(debounceKey, now);

    console.log(`[Socket] Real-time ${eventName} call received for this extension:`, data);
    callCenter.openScreenPop({
      data: {
        ...data,
        event: eventName,
        status: eventName,
      },
    });
  };

  // ۱. رویداد زنگ خوردن تلفن (ringing)
  useSocketEvent(
    'ringing',
    (data) => handleIncomingCall(data, 'ringing'),
    isActive,
  );

  // ۲. رویداد اسکرین‌پاپ اختصاصی (screen_pop)
  useSocketEvent(
    'screen_pop',
    (data) => {
      const evt = String(data?.event || data?.call?.event || '').toLowerCase().trim();
      if (evt === 'answered') {
        handleAnswered(data);
      } else if (evt === 'hangup' || evt === 'hangout') {
        handleHangup(data);
      } else {
        handleIncomingCall(data, 'ringing');
      }
    },
    isActive,
  );

  /**
   * بررسی اینکه آیا این تماس منحصراً توسط این اپراتور پاسخ داده شده است یا خیر
   */
  const didCurrentAgentAnswerCall = (data) => {
    if (!data) return false;

    // ۱. اولویت اول: بررسی بر اساس شماره داخلی پاسخ‌دهنده (دقیق‌ترین و قطعی‌ترین روش در تلفن سانترال)
    const eventExt = String(data.extension || data.call?.extension || '').trim();
    const myExts = (
      Array.isArray(callCenter?.myAgentExtensions) && callCenter.myAgentExtensions.length > 0
        ? callCenter.myAgentExtensions
        : [callCenter?.myAgentExtension]
    )
      .filter(Boolean)
      .map((e) => String(e).trim());

    if (eventExt && myExts.length > 0) {
      return myExts.includes(eventExt);
    }

    // ۲. بررسی بر اساس شناسه کاربری اپراتور
    const currentUserId = user?.id ? Number(user.id) : null;
    const answeredAgentIds = Array.isArray(data.agent_id)
      ? data.agent_id.map(Number)
      : (data.agent_id ? [Number(data.agent_id)] : []);

    if (data.call?.agent_user_id) {
      answeredAgentIds.push(Number(data.call.agent_user_id));
    }

    if (currentUserId && answeredAgentIds.length > 0) {
      if (answeredAgentIds.length === 1 && answeredAgentIds.includes(currentUserId)) {
        return true;
      }
    }

    return false;
  };

  // ۳. رویداد پاسخ داده شدن تماس (answered / answer)
  const handleAnswered = (data) => {
    if (!callCenter || !data) return;

    // جداسازی چندشرکتی
    const eventCompanyId = data.company_id || data.call?.company_id;
    const userCompanyId = user?.company_id;
    if (eventCompanyId && userCompanyId && String(eventCompanyId) !== String(userCompanyId)) {
      return;
    }

    const isAnsweredByMe = didCurrentAgentAnswerCall(data);

    if (isAnsweredByMe) {
      console.log('[Socket] Real-time answered event: answered by THIS operator:', data);

      const rawId = String(data.call_id || data.call?.id || data.uniqueid || data.simotel_uniqueid || '').trim();
      const callPhone = String(data.phone || data.call?.phone || '').trim();
      const now = Date.now();
      const debounceKey = `${rawId || 'call'}_${callPhone}_answered`;
      const lastSeen = recentEventsRef.current.get(debounceKey) || 0;
      if (now - lastSeen < 1000) {
        return;
      }
      recentEventsRef.current.set(debounceKey, now);

      const payload = {
        ...data,
        event: 'answered',
        status: 'answered',
        call: {
          ...(data.call || {}),
          event: 'answered',
          status: 'answered',
          extension: data.extension || data.call?.extension,
          agent_user_id: data.agent_id || data.call?.agent_user_id,
        },
      };

      // کارت برای اپراتور پاسخ‌دهنده باز می‌ماند و به وضعیت مکالمه جاری (حاشیه آبی) تغییر می‌یابد
      callCenter.openScreenPop({ data: payload });
    } else {
      console.log('[Socket] Real-time answered event: answered by ANOTHER operator. Closing popup for this agent:', data);

      // این تماس توسط همکار دیگری پاسخ داده شده است — بستن فوری تمام پاپ‌آپ‌های در حال زنگ این تماس برای سایر اپراتورها!
      callCenter.closeScreenPop(data);
    }
  };

  useSocketEvent('answered', handleAnswered, isActive);
  useSocketEvent('answer', handleAnswered, isActive);

  // ۴. رویداد قطع تماس (hangup / hangout)
  const handleHangup = (data) => {
    if (!callCenter || !data) return;

    // جداسازی چندشرکتی
    const eventCompanyId = data.company_id || data.call?.company_id;
    const userCompanyId = user?.company_id;
    if (eventCompanyId && userCompanyId && String(eventCompanyId) !== String(userCompanyId)) {
      return;
    }

    const rawId = String(data.call_id || data.call?.id || data.uniqueid || data.simotel_uniqueid || '').trim();
    const callPhone = String(data.phone || data.call?.phone || '').trim();

    if (rawId || callPhone) {
      for (const key of recentEventsRef.current.keys()) {
        if ((rawId && key.includes(rawId)) || (callPhone && key.includes(callPhone))) {
          recentEventsRef.current.delete(key);
        }
      }
    }

    const isAnsweredByMe = didCurrentAgentAnswerCall(data);
    const existingPop = callCenter.screenPops?.find((p) => isSameCall(p, data));
    const isExistingAnswered = existingPop && (
      existingPop.data?.event === 'answered' ||
      existingPop.data?.status === 'answered' ||
      existingPop.data?.call?.event === 'answered' ||
      existingPop.data?.call?.status === 'answered'
    );

    if (isAnsweredByMe || isExistingAnswered) {
      // این اپراتور در حال مکالمه بود؛ وضعیت کارت به پایان تماس (قرمز) جهت ثبت نتیجه تغییر می‌یابد
      const payload = {
        ...data,
        event: 'hangup',
        status: 'hangup',
        duration: data.duration ?? data.call?.duration,
        billsec: data.billsec ?? data.call?.billsec,
        call: {
          ...(data.call || {}),
          event: 'hangup',
          status: 'hangup',
          duration: data.duration ?? data.call?.duration,
          billsec: data.billsec ?? data.call?.billsec,
        },
      };

      callCenter.openScreenPop({ data: payload });
    } else {
      // تماس توسط فرد دیگری پاسخ داده شده بود یا پیش از پاسخ قطع شد — پاپ‌آپ در حال زنگ بسته می‌شود
      callCenter.closeScreenPop(data);
    }
  };

  useSocketEvent('hangup', handleHangup, isActive);
  useSocketEvent('hangout', handleHangup, isActive);

  useSocketEvent(
    'call_event',
    (data) => {
      const evt = String(data?.event || data?.call?.event || '').toLowerCase().trim();
      if (evt === 'answered' || evt === 'answer') {
        handleAnswered(data);
      } else if (evt === 'hangup' || evt === 'hangout') {
        handleHangup(data);
      } else if (evt === 'ringing') {
        handleIncomingCall(data, 'ringing');
      }
    },
    isActive,
  );

  if (!callCenter) return null;

  return <CallScreenPopModal />;
};

export default CallScreenPopPoller;
